import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Button,
  SkeletonBodyText,
  Modal
} from '@shopify/polaris';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { generateProfitInsight } from '../services/ai';
import ProfitGoal from '../components/ProfitGoal';
import ActionItems from '../components/ActionItems';
import ShareCard from '../components/ShareCard';
import ReferralBanner from '../components/ReferralBanner';
import ReviewPrompt from '../components/ReviewPrompt';
import { trackEvent as trackAnalyticsEvent } from '../utils/analytics';

const DASHBOARD_WINDOW_DAYS = 90;

const DEFAULT_DASHBOARD_SETTINGS = {
  currency: 'USD',
  alert_margin_threshold: 20,
  discount_margin_buffer_percent: 10,
  shipping_cost_alert_percent: 12,
  profit_target_percent: 25
};

const EMPTY_STATS = {
  synced_order_count: 0,
  total_orders: 0,
  total_revenue: 0,
  total_cogs: 0,
  total_gross_profit: 0,
  total_profit: 0,
  net_margin: 0,
  avg_margin: 0,
  negative_margin_count: 0,
  negative_margin_loss: 0,
  total_shipping: 0,
  total_fees: 0,
  total_discounts: 0,
  discount_buffer_breach_count: 0,
  discount_buffer_breach_amount: 0,
  high_shipping_count: 0,
  high_shipping_amount: 0,
  low_margin_product_count: 0,
  missing_cost_count: 0,
  estimated_monthly_profit_recovered: 0
};

// Sample data for the demo/no-shop view.
// All numbers are verified arithmetic — do not change without re-checking.
// net_profit = revenue - cogs - shipping - fees - discount
// #5027: 79 - 54 - 18.40 - 2.37 - 15 = -10.77, margin = -10.77/79*100 = -13.6%
// #5031: 129 - 89 - 22.40 - 3.87 - 25 = -11.27, margin = -11.27/129*100 = -8.7%
// #5042: 49 - 38 - 14.70 - 1.47 - 10 = -15.17, margin = -15.17/49*100 = -31.0%
const SAMPLE_NEGATIVE_ORDERS = [
  { order_number: '#5027', order_date: 'May 1', revenue: 79.00, cogs: 54.00, shipping: 18.40, fees: 2.37, discount: 15.00, net_profit: -10.77, margin_percent: -13.6 },
  { order_number: '#5031', order_date: 'May 6', revenue: 129.00, cogs: 89.00, shipping: 22.40, fees: 3.87, discount: 25.00, net_profit: -11.27, margin_percent: -8.7 },
  { order_number: '#5042', order_date: 'May 11', revenue: 49.00, cogs: 38.00, shipping: 14.70, fees: 1.47, discount: 10.00, net_profit: -15.17, margin_percent: -31.0 },
];

// margin = (price - cost) / price * 100
// Summer Tee Bundle: (49-41)/49*100 = 16.3%
// Logo Cap: (29-25)/29*100 = 13.8%
// Travel Mug: (35-29.50)/35*100 = 15.7%
// Phone Case 3-Pack: (39-34)/39*100 = 12.8%
// Enamel Pin Set: (24-21.50)/24*100 = 10.4%
const SAMPLE_LOW_MARGIN_PRODUCTS = [
  { title: 'Summer Tee Bundle', price: 49.00, cost: 41.00, margin_percent: 16.3 },
  { title: 'Logo Cap', price: 29.00, cost: 25.00, margin_percent: 13.8 },
  { title: 'Travel Mug', price: 35.00, cost: 29.50, margin_percent: 15.7 },
  { title: 'Phone Case 3-Pack', price: 39.00, cost: 34.00, margin_percent: 12.8 },
  { title: 'Enamel Pin Set', price: 24.00, cost: 21.50, margin_percent: 10.4 },
];

// Revenue total: 2010+2130+1980+1720 = 7840, shipping = 7840*0.114 = 893
const SAMPLE_WEEKLY_TREND = [
  { date: 'Week 1', profit: 980, revenue: 2010 },
  { date: 'Week 2', profit: 1140, revenue: 2130 },
  { date: 'Week 3', profit: 890, revenue: 1980 },
  { date: 'Week 4', profit: 1230, revenue: 1720 },
];
// Total leakage: negative-order loss (37.21) + discount breach (234) = 271.21
const SAMPLE_TOTAL_NEGATIVE_LOSS = 37.21;
const SAMPLE_DISCOUNT_LEAKAGE = 234.00;
const SAMPLE_SHIPPING_DRAG = 893.00;

const alertLabels = {
  product_margin_below_threshold: 'Low-margin product alert',
  order_margin_negative: 'Negative-margin order alert',
  discount_exceeds_margin_buffer: 'Discount buffer alert',
  shipping_cost_unusually_high: 'High shipping cost alert'
};

function toNumber(value, fallback = 0) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatPercent(value) {
  return `${toNumber(value).toFixed(1)}%`;
}

function getMarginTone(margin) {
  if (margin < 0) return 'critical';
  if (margin < 15) return 'warning';
  if (margin < 30) return 'info';
  return 'success';
}

function getSeverityTone(severity) {
  if (severity === 'high') return 'critical';
  if (severity === 'medium') return 'warning';
  return 'info';
}

function MetricCard({ label, value, detail, tone }) {
  return (
    <Card>
      <BlockStack gap="150">
        <Text variant="bodySm" as="p" tone="subdued">
          {label}
        </Text>
        <Text variant="headingXl" as="p" tone={tone}>
          {value}
        </Text>
        <Text variant="bodySm" as="p" tone="subdued">
          {detail}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function Dashboard({ shop, shopData, aiReady, showToast, onNavigate, refreshShopData }) {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [alerts, setAlerts] = useState([]);
  const [products, setProducts] = useState([]);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [worstMarginProducts, setWorstMarginProducts] = useState([]);
  const [negativeMarginOrders, setNegativeMarginOrders] = useState([]);
  const [dashboardSettings, setDashboardSettings] = useState(DEFAULT_DASHBOARD_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showNegativeMarginModal, setShowNegativeMarginModal] = useState(false);
  const [showWeeklyReportReminder, setShowWeeklyReportReminder] = useState(false);

  const loadDashboard = async () => {
    if (!shop) {
      setLoading(false);
      return;
    }

    setLoadError(null);

    try {
      const [dashResponse, productsResponse] = await Promise.all([
        fetch(`/api/dashboard?shop=${shop}`).catch((error) => {
          console.error('Dashboard fetch error:', error);
          return null;
        }),
        fetch(`/api/products?shop=${shop}`).catch((error) => {
          console.error('Products fetch error:', error);
          return null;
        })
      ]);

      if (dashResponse && dashResponse.ok) {
        const data = await dashResponse.json();
        const nextStats = data.stats && typeof data.stats === 'object'
          ? { ...EMPTY_STATS, ...data.stats }
          : EMPTY_STATS;

        setStats(nextStats);
        setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
        setWorstMarginProducts(Array.isArray(data.worst_margin_products) ? data.worst_margin_products : []);
        setNegativeMarginOrders(Array.isArray(data.negative_margin_orders) ? data.negative_margin_orders : []);
        setDashboardSettings({
          ...DEFAULT_DASHBOARD_SETTINGS,
          ...(shopData?.settings || {}),
          ...(data.settings || {})
        });

        if (Array.isArray(data.weekly_trend)) {
          setWeeklyTrend(data.weekly_trend.map((week) => ({
            date: new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            profit: toNumber(week.profit),
            revenue: toNumber(week.revenue),
            orders: parseInt(week.orders || 0, 10),
            netMargin: toNumber(week.net_margin)
          })));
        } else {
          setWeeklyTrend([]);
        }

      } else if (dashResponse) {
        const errorData = await dashResponse.json().catch(() => ({}));
        const message = errorData.message || errorData.error || `Server error (${dashResponse.status})`;
        setLoadError(message);
        setStats(EMPTY_STATS);
        setAlerts([]);
        setWorstMarginProducts([]);
        setNegativeMarginOrders([]);
      } else {
        setLoadError('Failed to connect to the dashboard service. Please check your connection.');
        setStats(EMPTY_STATS);
        setAlerts([]);
        setWorstMarginProducts([]);
        setNegativeMarginOrders([]);
      }

      if (productsResponse && productsResponse.ok) {
        const productsData = await productsResponse.json();
        setProducts(Array.isArray(productsData) ? productsData : []);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setLoadError('Failed to load dashboard data. Please try again.');
      setStats(EMPTY_STATS);
      setAlerts([]);
      setProducts([]);
      setWorstMarginProducts([]);
      setNegativeMarginOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [shop, aiReady, shopData?.settings]);

  useEffect(() => {
    let cancelled = false;

    if (!aiReady || parseInt(stats?.total_orders || 0, 10) <= 0) {
      setAiInsight(null);
      setInsightLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadInsight = async () => {
      setInsightLoading(true);
      try {
        const insight = await generateProfitInsight(stats);
        if (!cancelled) {
          setAiInsight(insight);
        }
      } catch (error) {
        console.error('Insight generation failed:', error);
        if (!cancelled) {
          setAiInsight(null);
        }
      } finally {
        if (!cancelled) {
          setInsightLoading(false);
        }
      }
    };

    loadInsight();

    return () => {
      cancelled = true;
    };
  }, [aiReady, stats]);

  useEffect(() => {
    const negativeCount = parseInt(stats?.negative_margin_count || 0, 10);
    if (negativeCount > 0) {
      const alreadyShown = localStorage.getItem('profitpulse_negative_margin_modal_shown');
      if (!alreadyShown) {
        setShowNegativeMarginModal(true);
        localStorage.setItem('profitpulse_negative_margin_modal_shown', 'true');
      }
    }
  }, [stats]);

  useEffect(() => {
    if (!shop) return;
    const today = new Date().toISOString().split('T')[0];
    const visitDays = JSON.parse(localStorage.getItem('profitpulse_visit_days') || '[]');
    if (!visitDays.includes(today)) {
      visitDays.push(today);
      localStorage.setItem('profitpulse_visit_days', JSON.stringify(visitDays));
    }
  }, [shop]);

  const trackEvent = (event, data = {}) => {
    trackAnalyticsEvent(event, data, { shop });
  };

  useEffect(() => {
    if (!shop || loading) return;
    const negativeCount = parseInt(stats?.negative_margin_count || 0, 10);
    const missingCount = parseInt(stats?.missing_cost_count || 0, 10);
    const totalOrds = parseInt(stats?.total_orders || 0, 10);

    trackEvent('profit_leak_review_viewed', { negative_margin_count: negativeCount, total_orders: totalOrds });

    const leakCount =
      negativeCount +
      parseInt(stats?.low_margin_product_count || 0, 10) +
      parseInt(stats?.discount_buffer_breach_count || 0, 10) +
      parseInt(stats?.high_shipping_count || 0, 10);

    if (leakCount > 0) {
      const alreadyTracked = localStorage.getItem('profitpulse_first_leak_tracked');
      if (!alreadyTracked) {
        trackEvent('first_margin_leak_found', {
          negative_margin_count: negativeCount,
          low_margin_product_count: parseInt(stats?.low_margin_product_count || 0, 10),
          discount_buffer_breach_count: parseInt(stats?.discount_buffer_breach_count || 0, 10),
          high_shipping_count: parseInt(stats?.high_shipping_count || 0, 10)
        });
        localStorage.setItem('profitpulse_first_leak_tracked', 'true');
      }
    }

    if (negativeCount > 0) {
      trackEvent('profit_leak_flagged', { count: negativeCount, type: 'negative_margin' });
    }

    if (missingCount > 0) {
      trackEvent('cogs_gap_detected', { missing_cost_count: missingCount });
    }

    if (totalOrds > 0) {
      const alreadyTracked = localStorage.getItem('profitpulse_first_value_tracked');
      if (!alreadyTracked) {
        trackEvent('first_value_reached', { total_orders: totalOrds });
        localStorage.setItem('profitpulse_first_value_tracked', 'true');
      }
    }
  }, [shop, loading, stats]);

  useEffect(() => {
    if (!shop || loading) return;
    if (shopData?.subscription_tier === 'pro') return;
    const totalOrds = parseInt(stats?.total_orders || 0, 10);
    if (totalOrds <= 0) return;
    if (sessionStorage.getItem('profitpulse_first_value_paywall_viewed')) return;
    trackEvent('first_value_paywall_viewed', {
      current_tier: shopData?.subscription_tier || 'free',
      total_orders: totalOrds
    });
    sessionStorage.setItem('profitpulse_first_value_paywall_viewed', '1');
  }, [shop, loading, stats, shopData?.subscription_tier]);

  // Show weekly report reminder when 7+ days have passed since the last export
  useEffect(() => {
    if (!shop || loading) return;
    const lastExportTs = localStorage.getItem('profitpulse_last_report_export_ts');
    if (!lastExportTs) return;
    const daysSince = (Date.now() - new Date(lastExportTs).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince >= 7) {
      setShowWeeklyReportReminder(true);
      trackEvent('weekly_report_reminded', { days_since_last_export: Math.floor(daysSince) });
    }
  }, [shop, loading]);

  // Track demo dashboard view once at mount (only when no shop is connected)
  useEffect(() => {
    if (shop) return;
    try {
      trackAnalyticsEvent('demo_margin_dashboard_viewed');
    } catch {}
  }, []); // intentional empty deps — run once at mount for demo tracking

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      const response = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      if (response.ok) {
        const result = await response.json();
        await Promise.all([
          loadDashboard(),
          refreshShopData ? refreshShopData() : Promise.resolve()
        ]);
        showToast(result.message || `Sync complete! ${result.products_synced} products and ${result.orders_synced} orders refreshed.`);
      } else {
        const error = await response.json().catch(() => ({}));
        if (error.needsReinstall) {
          window.location.href = `/auth?shop=${shop}`;
          return;
        }
        showToast(error.error || 'Sync failed', true);
      }
    } catch (error) {
      console.error('Sync error:', error);
      showToast('Sync failed. Please try again.', true);
    } finally {
      setSyncing(false);
    }
  };

  const handleOptimizePricing = async () => {
    setOptimizing(true);
    try {
      const response = await fetch('/api/optimize-pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.recommendations > 0) {
          showToast(`Profit leak scan complete. ${result.recommendations} recommendation(s) found.`);
        } else {
          showToast('Leak scan complete. No urgent pricing changes were found.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.message || errorData.error || 'Leak scan failed. Please try again.', true);
      }
    } catch (error) {
      console.error('Pricing optimization error:', error);
      showToast('Failed to connect to the recommendation service.', true);
    } finally {
      setOptimizing(false);
    }
  };

  const handleExportReport = () => {
    trackEvent('margin_report_exported');
    localStorage.setItem('profitpulse_last_report_export_ts', new Date().toISOString());
    setShowWeeklyReportReminder(false);
    window.open(`/api/export?type=founder_report&shop=${shop}`, '_blank');
  };

  if (!shop) {
    const handleDemoCtaClick = () => trackEvent('find_margin_leaks_clicked');

    return (
      <Page title="Profit Leak Detection" subtitle="Sample data — see what Profit Pulse AI finds in a typical Shopify store">
        <Layout>
          <Layout.Section>
            <Banner title="This is sample data" tone="info">
              <p>
                The analysis below shows the kind of profit leaks Profit Pulse AI finds in Shopify stores.
                Connect your store to run this on your real orders and products.
              </p>
              <div style={{ marginTop: '8px' }}>
                <InlineStack gap="200">
                  <Button
                    variant="primary"
                    url="https://apps.shopify.com/profit-pulse-ai"
                    external
                    onClick={handleDemoCtaClick}
                  >
                    Find my margin leaks
                  </Button>
                  <Button
                    url="https://apps.shopify.com/profit-pulse-ai"
                    external
                    onClick={handleDemoCtaClick}
                  >
                    Run this on my store
                  </Button>
                </InlineStack>
              </div>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <MetricCard label="Orders analyzed" value="47" detail="Sample store — last 30 days" />
              <MetricCard label="Products analyzed" value="28" detail="Active variants with costs entered" />
              <MetricCard label="Negative-margin orders" value="3" detail={`$${SAMPLE_TOTAL_NEGATIVE_LOSS.toFixed(2)} already lost`} tone="critical" />
              <MetricCard label="Low-margin products" value="5" detail="Below 20% margin threshold" tone="warning" />
              <MetricCard label="Discount leakage" value={`$${SAMPLE_DISCOUNT_LEAKAGE.toFixed(2)}`} detail="8 orders exceeded the buffer" tone="warning" />
              <MetricCard label="Shipping cost drag" value={`$${SAMPLE_SHIPPING_DRAG.toFixed(2)}`} detail="11.4% of revenue — above alert threshold" tone="warning" />
              <MetricCard label="Estimated total leakage" value={`$${(SAMPLE_TOTAL_NEGATIVE_LOSS + SAMPLE_DISCOUNT_LEAKAGE).toFixed(2)}`} detail="Negative orders + discount breach" tone="critical" />
            </div>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="start">
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">Negative-margin orders found</Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      Orders that lost money after COGS, shipping, fees, and discounts
                    </Text>
                  </BlockStack>
                  <Badge tone="critical">3 found</Badge>
                </InlineStack>
                <BlockStack gap="300">
                  {SAMPLE_NEGATIVE_ORDERS.map((order) => (
                    <Card key={order.order_number}>
                      <InlineStack align="space-between" blockAlign="start" gap="300" wrap>
                        <BlockStack gap="100">
                          <InlineStack gap="200" blockAlign="center">
                            <Text variant="headingSm" as="h3">{order.order_number}</Text>
                            <Badge tone="critical">{order.margin_percent.toFixed(1)}%</Badge>
                          </InlineStack>
                          <Text variant="bodySm" as="p" tone="subdued">{order.order_date}</Text>
                        </BlockStack>
                        <InlineStack gap="400" wrap>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Profit lost</Text>
                            <Text variant="bodyMd" as="p" tone="critical">
                              ${Math.abs(order.net_profit).toFixed(2)}
                            </Text>
                          </BlockStack>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Discounts</Text>
                            <Text variant="bodyMd" as="p">${order.discount.toFixed(2)}</Text>
                          </BlockStack>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Shipping</Text>
                            <Text variant="bodyMd" as="p">${order.shipping.toFixed(2)}</Text>
                          </BlockStack>
                        </InlineStack>
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="start">
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">Low-margin products flagged</Text>
                    <Text variant="bodySm" as="p" tone="subdued">
                      Products below the 20% margin threshold — fix before orders compound the leak
                    </Text>
                  </BlockStack>
                  <Badge tone="warning">5 products</Badge>
                </InlineStack>
                <BlockStack gap="300">
                  {SAMPLE_LOW_MARGIN_PRODUCTS.map((product) => (
                    <Card key={product.title}>
                      <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                        <BlockStack gap="100">
                          <Text variant="headingSm" as="h3">{product.title}</Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            Sells for ${product.price.toFixed(2)} — cost ${product.cost.toFixed(2)}
                          </Text>
                        </BlockStack>
                        <Badge tone={product.margin_percent < 12 ? 'critical' : 'warning'}>
                          {product.margin_percent.toFixed(1)}% margin
                        </Badge>
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Margin trend (sample)</Text>
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <LineChart data={SAMPLE_WEEKLY_TREND}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="profit" stroke="#008060" strokeWidth={2} name="Profit" />
                      <Line type="monotone" dataKey="revenue" stroke="#5c6ac4" strokeWidth={1.5} strokeDasharray="5 5" name="Revenue" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="heading2xl" as="h1">Run this on your store.</Text>
                <Text variant="bodyLg" as="p" tone="subdued">
                  Profit Pulse AI analyzes true order-level profit after COGS, shipping, fees, and discounts.
                  Connect your Shopify store to find your actual margin leaks.
                </Text>
                <InlineStack gap="200" wrap>
                  <Button
                    variant="primary"
                    size="large"
                    url="https://apps.shopify.com/profit-pulse-ai"
                    external
                    onClick={handleDemoCtaClick}
                  >
                    Find my margin leaks
                  </Button>
                  <Button
                    size="large"
                    url="https://apps.shopify.com/profit-pulse-ai"
                    external
                    onClick={handleDemoCtaClick}
                  >
                    Run this on my store
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title="Profit Leak Detection">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={12} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const settings = {
    ...DEFAULT_DASHBOARD_SETTINGS,
    ...(shopData?.settings || {}),
    ...(dashboardSettings || {})
  };
  const currency = settings.currency || 'USD';

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(toNumber(value));

  const totalRevenue = toNumber(stats.total_revenue);
  const totalCogs = toNumber(stats.total_cogs);
  const totalGrossProfit = toNumber(stats.total_gross_profit);
  const totalProfit = toNumber(stats.total_profit);
  const netMargin = toNumber(stats.net_margin);
  const avgMargin = toNumber(stats.avg_margin);
  const syncedOrderCount = parseInt(stats.synced_order_count || 0, 10);
  const totalOrders = parseInt(stats.total_orders || 0, 10);
  const negativeMarginCount = parseInt(stats.negative_margin_count || 0, 10);
  const negativeMarginLoss = toNumber(stats.negative_margin_loss);
  const totalShipping = toNumber(stats.total_shipping);
  const totalFees = toNumber(stats.total_fees);
  const totalDiscounts = toNumber(stats.total_discounts);
  const lowMarginProductCount = parseInt(stats.low_margin_product_count || 0, 10);
  const missingCostCount = parseInt(stats.missing_cost_count || 0, 10);
  const estimatedRecovery = toNumber(stats.estimated_monthly_profit_recovered);
  const discountBreachCount = parseInt(stats.discount_buffer_breach_count || 0, 10);
  const highShippingCount = parseInt(stats.high_shipping_count || 0, 10);
  const lastSyncAt = shopData?.last_sync_at ? new Date(shopData.last_sync_at) : null;
  const shippingRatio = totalRevenue > 0 ? (totalShipping / totalRevenue) * 100 : 0;
  const feeRatio = totalRevenue > 0 ? (totalFees / totalRevenue) * 100 : 0;
  const discountRatio = totalRevenue > 0 ? (totalDiscounts / totalRevenue) * 100 : 0;
  const productsWithCost = products.filter((product) => product.cost || product.cost === 0);
  const missingCostProducts = products.filter((product) => !product.cost && product.cost !== 0);

  return (
    <Page
      title="Profit Leak Detection"
      subtitle={`Order-level margin protection for the last ${DASHBOARD_WINDOW_DAYS} days`}
      primaryAction={{
        content: optimizing ? 'Scanning...' : 'Run Leak Scan',
        onAction: handleOptimizePricing,
        loading: optimizing,
        disabled: optimizing
      }}
      secondaryActions={[
        {
          content: syncing ? 'Syncing...' : 'Sync Data',
          onAction: handleSyncData,
          loading: syncing,
          disabled: syncing
        },
        {
          content: 'Export Report',
          onAction: handleExportReport
        }
      ]}
    >
      <Layout>
        {loadError && (
          <Layout.Section>
            <Banner title="Failed to load dashboard data" tone="critical">
              <p>{loadError}</p>
              <div style={{ marginTop: '8px' }}>
                <Button onClick={() => window.location.reload()}>Retry</Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {showWeeklyReportReminder && (
          <Layout.Section>
            <Banner
              title="Your weekly margin leak report is ready"
              tone="info"
              onDismiss={() => {
                setShowWeeklyReportReminder(false);
                localStorage.setItem('profitpulse_last_report_export_ts', new Date().toISOString());
              }}
            >
              <p>It's been 7+ days since your last profit leak report. Download a fresh one to review negative-margin orders, discount leakage, and shipping drag for the past 90 days.</p>
              <div style={{ marginTop: '8px' }}>
                <Button onClick={handleExportReport}>Download weekly report</Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="200" wrap>
                <Badge tone="success">True profit per order</Badge>
                <Badge tone="info">Browser-first analysis</Badge>
                <Badge tone="info">Privacy-forward</Badge>
              </InlineStack>
              <BlockStack gap="200">
                <Text variant="heading2xl" as="h1">
                  Find profit leaks before they cost you money.
                </Text>
                <Text variant="bodyLg" as="p" tone="subdued">
                  Profit Pulse AI analyzes true order-level profit after COGS, shipping, fees, and discounts so merchants can see which products and orders are actually making money.
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Last Shopify refresh: {lastSyncAt ? lastSyncAt.toLocaleString() : 'Not synced yet'}
                </Text>
              </BlockStack>
              <InlineStack gap="400" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Orders analyzed</Text>
                  <Text variant="headingLg" as="p">
                    {totalOrders}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">At-risk orders</Text>
                  <Text variant="headingLg" as="p" tone={negativeMarginCount > 0 ? 'critical' : 'success'}>
                    {negativeMarginCount}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Low-margin products</Text>
                  <Text variant="headingLg" as="p" tone={lowMarginProductCount > 0 ? 'warning' : 'success'}>
                    {lowMarginProductCount}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Discount leakage</Text>
                  <Text variant="headingLg" as="p" tone={discountBreachCount > 0 ? 'warning' : undefined}>
                    {formatCurrency(totalDiscounts)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Estimated recovery</Text>
                  <Text variant="headingLg" as="p" tone={estimatedRecovery > 0 ? 'success' : undefined}>
                    {formatCurrency(estimatedRecovery)}
                  </Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <MetricCard label="Revenue" value={formatCurrency(totalRevenue)} detail={`${totalOrders} orders analyzed`} />
            <MetricCard label="Gross profit" value={formatCurrency(totalGrossProfit)} detail="After COGS, before shipping, fees, and discounts" tone="success" />
            <MetricCard label="Net margin" value={formatPercent(netMargin)} detail={`${formatCurrency(totalProfit)} net profit after all costs`} tone={getMarginTone(netMargin)} />
            <MetricCard label="Negative-margin orders" value={String(negativeMarginCount)} detail={negativeMarginCount > 0 ? `${formatCurrency(negativeMarginLoss)} already lost` : 'No orders below zero'} tone={negativeMarginCount > 0 ? 'critical' : 'success'} />
            <MetricCard label="Worst-margin products" value={String(lowMarginProductCount)} detail={lowMarginProductCount > 0 ? `Below your ${settings.alert_margin_threshold}% threshold` : 'No products under the threshold'} tone={lowMarginProductCount > 0 ? 'warning' : 'success'} />
            <MetricCard label="Discount leakage" value={formatCurrency(totalDiscounts)} detail={discountBreachCount > 0 ? `${discountBreachCount} orders exceeded your buffer` : `${formatPercent(discountRatio)} of revenue discounted`} tone={discountBreachCount > 0 ? 'warning' : undefined} />
            <MetricCard label="Shipping cost impact" value={formatCurrency(totalShipping)} detail={highShippingCount > 0 ? `${highShippingCount} orders crossed the shipping rule` : `${formatPercent(shippingRatio)} of revenue`} tone={highShippingCount > 0 ? 'warning' : undefined} />
            <MetricCard label="Estimated monthly profit recovered" value={formatCurrency(estimatedRecovery)} detail="Modeled from detected leak patterns" tone={estimatedRecovery > 0 ? 'success' : undefined} />
          </div>
        </Layout.Section>

        {!loadError && totalOrders > 0 && (negativeMarginCount > 0 || lowMarginProductCount > 0) && (
          <Layout.Section>
            <Banner title="Analysis complete" tone="success">
              <p>
                {negativeMarginCount > 0 && (
                  `We found ${negativeMarginCount} negative-margin order${negativeMarginCount === 1 ? '' : 's'}. `
                )}
                {lowMarginProductCount > 0 && (
                  `We found ${lowMarginProductCount} product${lowMarginProductCount === 1 ? '' : 's'} below your ${settings.alert_margin_threshold}% margin target. `
                )}
                Review the sections below to protect your margins.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {!loadError && totalOrders > 0 && shopData?.subscription_tier !== 'pro' && (
          <Layout.Section>
            <Banner title="Keep monitoring the leaks this scan found" tone="info">
              <p>
                Pro unlocks the full 90-day history, action items, goal tracking, exports, and ongoing negative-margin alerts from this dashboard.
              </p>
              <div style={{ marginTop: '8px' }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    trackEvent('dashboard_upgrade_clicked', {
                      current_tier: shopData?.subscription_tier || 'free',
                      total_orders: totalOrders
                    });
                    onNavigate('settings');
                  }}
                >
                  Review Pro plan
                </Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {!loadError && products.length > 0 && productsWithCost.length === 0 && (
          <Layout.Section>
            <Banner title="You're still seeing revenue before true profit." tone="warning">
              <p>
                Add product costs to unlock order-level profit after COGS, shipping, fees, and discounts.
              </p>
              <div style={{ marginTop: '8px' }}>
                <Button variant="primary" onClick={() => onNavigate('products')}>
                  Add costs now
                </Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {!loadError && productsWithCost.length > 0 && missingCostProducts.length > 0 && (
          <Layout.Section>
            <Banner title={`${missingCostProducts.length} product${missingCostProducts.length === 1 ? '' : 's'} still missing costs`} tone="warning">
              <p>
                Margin protection is strongest when every product has a cost. Add the remaining costs to tighten your true profit calculations.
              </p>
              <div style={{ marginTop: '8px' }}>
                <Button onClick={() => onNavigate('products')}>Finish cost coverage</Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {!loadError && syncedOrderCount === 0 && (
          <Layout.Section>
            <Banner title="No orders synced yet" tone="warning">
              <p>
                Sync your Shopify data to start detecting negative-margin orders, discount leakage, shipping drag, and low-margin products.
              </p>
              <div style={{ marginTop: '8px' }}>
                <Button onClick={handleSyncData} loading={syncing}>
                  {syncing ? 'Syncing...' : 'Sync from Shopify'}
                </Button>
              </div>
            </Banner>
          </Layout.Section>
        )}

        {!loadError && syncedOrderCount > 0 && totalOrders === 0 && (
          <Layout.Section>
            <Banner title={`No orders in the last ${DASHBOARD_WINDOW_DAYS} days`} tone="info">
              <p>
                {`You've already synced ${syncedOrderCount} order${syncedOrderCount === 1 ? '' : 's'}, but none fall inside the current dashboard window. Review older orders in the Orders tab or create a fresh test order in Shopify.`}
              </p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">Margin Protection Alerts</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Rules watching product margins, negative orders, discounts, and shipping cost drag.
                  </Text>
                </BlockStack>
                <Badge tone={alerts.length > 0 ? 'warning' : 'success'}>
                  {alerts.length > 0 ? `${alerts.length} active` : 'All clear'}
                </Badge>
              </InlineStack>

              {alerts.length > 0 ? (
                <BlockStack gap="300">
                  {alerts.map((alert) => (
                    <Card key={alert.id}>
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <InlineStack gap="200" blockAlign="center">
                            <Badge tone={getSeverityTone(alert.severity)}>
                              {alertLabels[alert.alert_type] || 'Alert'}
                            </Badge>
                            <Text variant="headingSm" as="h3">
                              {alert.severity === 'high' ? 'Needs attention' : 'Watch closely'}
                            </Text>
                          </InlineStack>
                          <Text variant="bodySm" as="span" tone="subdued">
                            Rule active
                          </Text>
                        </InlineStack>
                        <Text as="p">{alert.message}</Text>
                      </BlockStack>
                    </Card>
                  ))}
                </BlockStack>
              ) : (
                <Banner tone="success">
                  <p>{`Your current rules are not detecting any urgent profit leaks in the last ${DASHBOARD_WINDOW_DAYS} days.`}</p>
                </Banner>
              )}

              <InlineStack gap="200" wrap>
                <Badge tone="info">Product margin below {settings.alert_margin_threshold}%</Badge>
                <Badge tone="info">Order margin below 0%</Badge>
                <Badge tone="info">Discounts above {settings.discount_margin_buffer_percent}%</Badge>
                <Badge tone="info">Shipping above {settings.shipping_cost_alert_percent}%</Badge>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {aiInsight && (
          <Layout.Section>
            <Banner title="AI recommendation" tone="info">
              <p>{aiInsight}</p>
              <Text variant="bodySm" as="p" tone="subdued">
                Generated in your browser when possible to keep your margin analysis private.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {insightLoading && (
          <Layout.Section>
            <Banner tone="info">
              <Text as="p">Scanning your latest profit leak patterns...</Text>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">1. True profit per order</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Revenue alone hides margin drag. This view keeps COGS, shipping, fees, and discounts in one place.
                  </Text>
                </BlockStack>
                <Button onClick={() => onNavigate('orders')}>Review orders</Button>
              </InlineStack>

              <InlineStack gap="600" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Net profit</Text>
                  <Text variant="headingLg" as="p" tone={totalProfit >= 0 ? 'success' : 'critical'}>
                    {formatCurrency(totalProfit)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">COGS tracked</Text>
                  <Text variant="headingLg" as="p">{formatCurrency(totalCogs)}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Shipping + fees</Text>
                  <Text variant="headingLg" as="p">
                    {formatCurrency(totalShipping + totalFees)}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" as="span" tone="subdued">Discount drag</Text>
                  <Text variant="headingLg" as="p" tone={totalDiscounts > 0 ? 'warning' : undefined}>
                    {formatCurrency(totalDiscounts)}
                  </Text>
                </BlockStack>
              </InlineStack>

              <Text as="p" tone="subdued">
                Net margin is currently {formatPercent(netMargin)}. Shipping is {formatPercent(shippingRatio)} of revenue and payment fees are {formatPercent(feeRatio)}.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">2. Low-margin product alerts</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Products below your guardrail are easier to fix before they create a bigger order-level problem.
                  </Text>
                </BlockStack>
                <Button onClick={() => onNavigate('products')}>Review products</Button>
              </InlineStack>

              {worstMarginProducts.length > 0 ? (
                <BlockStack gap="300">
                  {worstMarginProducts.map((product) => (
                    <Card key={product.variant_id}>
                      <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                        <BlockStack gap="100">
                          <Text variant="headingSm" as="h3">{product.title}</Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            Unit profit {formatCurrency(product.unit_profit)} on {formatCurrency(product.price)} sale price
                          </Text>
                        </BlockStack>
                        <InlineStack gap="200" blockAlign="center">
                          <Badge tone={getMarginTone(toNumber(product.margin_percent))}>
                            {formatPercent(product.margin_percent)}
                          </Badge>
                          <Text variant="bodySm" as="span" tone="subdued">
                            Threshold {settings.alert_margin_threshold}%
                          </Text>
                        </InlineStack>
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>
              ) : (
                <Banner tone="success">
                  <p>No low-margin products are currently flagged. Keep monitoring costs as suppliers or shipping rates change.</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">3. Negative-margin order detection</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Orders below zero are the fastest signal that pricing, discounting, or shipping needs attention.
                  </Text>
                </BlockStack>
                <Button onClick={() => onNavigate('orders')}>Open orders</Button>
              </InlineStack>

              {negativeMarginOrders.length > 0 ? (
                <BlockStack gap="300">
                  {negativeMarginOrders.map((order) => (
                    <Card key={`${order.order_number}-${order.order_date}`}>
                      <InlineStack align="space-between" blockAlign="start" gap="300" wrap>
                        <BlockStack gap="100">
                          <InlineStack gap="200" blockAlign="center">
                            <Text variant="headingSm" as="h3">{order.order_number}</Text>
                            <Badge tone="critical">{formatPercent(order.margin_percent)}</Badge>
                          </InlineStack>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {new Date(order.order_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Text>
                        </BlockStack>
                        <InlineStack gap="400" wrap>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Profit lost</Text>
                            <Text variant="bodyMd" as="p" tone="critical">
                              {formatCurrency(order.net_profit)}
                            </Text>
                          </BlockStack>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Discounts</Text>
                            <Text variant="bodyMd" as="p">{formatCurrency(order.total_discounts)}</Text>
                          </BlockStack>
                          <BlockStack gap="100">
                            <Text variant="bodySm" as="span" tone="subdued">Shipping</Text>
                            <Text variant="bodyMd" as="p">{formatCurrency(order.shipping_cost)}</Text>
                          </BlockStack>
                        </InlineStack>
                      </InlineStack>
                    </Card>
                  ))}
                </BlockStack>
              ) : (
                <Banner tone="success">
                  <p>{`No negative-margin orders were found in the last ${DASHBOARD_WINDOW_DAYS} days.`}</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <ProfitGoal stats={stats} shopData={{ ...shopData, settings }} onNavigate={onNavigate} />
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">5. Margin trend dashboard</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Track whether margin protection is improving over time, not just whether revenue is growing.
                  </Text>
                </BlockStack>
                <Badge tone={getMarginTone(netMargin)}>
                  Current net margin {formatPercent(netMargin)}
                </Badge>
              </InlineStack>

              {weeklyTrend.length > 0 ? (
                <>
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <LineChart data={weeklyTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => (
                            name === 'Profit' || name === 'Revenue'
                              ? formatCurrency(value)
                              : `${toNumber(value).toFixed(1)}%`
                          )}
                        />
                        <Line type="monotone" dataKey="profit" stroke="#008060" strokeWidth={2} name="Profit" />
                        <Line type="monotone" dataKey="revenue" stroke="#5c6ac4" strokeWidth={1.5} strokeDasharray="5 5" name="Revenue" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <InlineStack gap="600" wrap>
                    <BlockStack gap="100">
                      <Text variant="bodySm" as="span" tone="subdued">Net margin</Text>
                      <Text variant="headingMd" as="p">{formatPercent(netMargin)}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text variant="bodySm" as="span" tone="subdued">Average order margin</Text>
                      <Text variant="headingMd" as="p">{formatPercent(avgMargin)}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text variant="bodySm" as="span" tone="subdued">Orders tracked</Text>
                      <Text variant="headingMd" as="p">{totalOrders}</Text>
                    </BlockStack>
                  </InlineStack>
                </>
              ) : (
                <Banner tone="info">
                  <p>Your trend dashboard will populate once order history is synced.</p>
                </Banner>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <ActionItems
            shop={shop}
            stats={stats}
            products={products}
            shopData={{ ...shopData, settings }}
            onNavigate={onNavigate}
            showToast={showToast}
          />
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Privacy-forward by design</Text>
              <Text as="p" tone="subdued">
                Profit Pulse AI runs recommendation summaries in your browser where possible, avoids sending store data to external AI APIs by default, and is explicit about what it reads from Shopify.
              </Text>
              <InlineStack gap="600" wrap>
                <BlockStack gap="100">
                  <Text variant="headingSm" as="h3">What we access</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Order totals, shipping, fees, discounts, dates, product titles, prices, and the product costs you enter.
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="headingSm" as="h3">What stays local</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    Browser-cached AI models, dismissal state, visit history, and recommendation generation where supported.
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="headingSm" as="h3">What we avoid</Text>
                  <Text variant="bodySm" as="p" tone="subdued">
                    No unnecessary external AI calls and no customer PII pulled into the dashboard experience.
                  </Text>
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <ReviewPrompt shop={shop} />
        </Layout.Section>

        <Layout.Section>
          <InlineStack gap="400" wrap>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <ShareCard stats={stats} shopData={shopData} showToast={showToast} />
            </div>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <ReferralBanner shop={shop} shopData={shopData} showToast={showToast} />
            </div>
          </InlineStack>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text variant="headingMd" as="h2">Browser AI status</Text>
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={aiReady ? 'success' : 'info'}>
                  {aiReady ? 'Browser AI active' : 'Browser AI loading'}
                </Badge>
                <Text variant="bodySm" as="span" tone="subdued">
                  Insights are generated on-device when available to keep margin analysis private.
                </Text>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showNegativeMarginModal}
        onClose={() => setShowNegativeMarginModal(false)}
        title="Profit leak detected"
        primaryAction={{
          content: 'Inspect at-risk orders',
          onAction: () => {
            setShowNegativeMarginModal(false);
            onNavigate('orders');
          }
        }}
        secondaryActions={[
          {
            content: 'Dismiss',
            onAction: () => setShowNegativeMarginModal(false)
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyLg">
              {negativeMarginCount === 1
                ? 'One recent order fell below a 0% margin after COGS, shipping, fees, and discounts.'
                : `${negativeMarginCount} recent orders fell below a 0% margin after COGS, shipping, fees, and discounts.`}
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              You have already leaked {formatCurrency(negativeMarginLoss)} across those orders. Review them now so the same products, discount rules, or shipping patterns do not keep compounding.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
